import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { generateEnterpriseRecommendations } from '../../../services/aiRecommendationsService';
import toast from 'react-hot-toast';

const EnterpriseAIRecommendationsPanel = ({ workforceData }) => {
  const [recommendations, setRecommendations] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('wellness');

  const fetchRecommendations = async () => {
    setIsLoading(true);
    try {
      const data = await generateEnterpriseRecommendations(workforceData);
      setRecommendations(data);
    } catch (error) {
      toast?.error('Failed to generate AI recommendations');
      console.error('AI recommendations error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (workforceData) {
      fetchRecommendations();
    }
  }, []);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'var(--color-error)';
      case 'medium': return 'var(--color-warning)';
      case 'low': return 'var(--color-success)';
      default: return 'var(--color-muted)';
    }
  };

  const getEffortColor = (effort) => {
    switch (effort) {
      case 'high': return 'var(--color-error)';
      case 'medium': return 'var(--color-warning)';
      case 'low': return 'var(--color-success)';
      default: return 'var(--color-muted)';
    }
  };

  const tabs = [
    { id: 'wellness', label: 'Employee Wellness', icon: 'Heart' },
    { id: 'risks', label: 'Risk Mitigation', icon: 'Shield' },
    { id: 'engagement', label: 'Engagement', icon: 'Users' }
  ];

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <Icon name="Brain" size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Workforce Insights</h3>
            <p className="text-sm text-muted-foreground">Employee wellness analytics powered by AI</p>
          </div>
        </div>
        <Button
          onClick={fetchRecommendations}
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          <Icon name="RefreshCw" size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth whitespace-nowrap ${
              activeTab === tab?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            {tab?.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="Loader2" size={40} className="text-primary animate-spin mb-4" />
          <p className="text-muted-foreground">Analyzing workforce data...</p>
        </div>
      ) : recommendations ? (
        <div className="space-y-4">
          {activeTab === 'wellness' && (
            <div className="space-y-3">
              {recommendations?.wellness?.map((item, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg border border-border bg-background hover:shadow-md transition-smooth"
                >
                  <h4 className="font-semibold text-foreground mb-2">{item?.title}</h4>
                  <p className="text-sm text-muted-foreground mb-3">{item?.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="TrendingUp" size={16} className="text-success" />
                      <span className="font-medium text-success">Impact: {item?.impact}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {item?.departments?.map((dept, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 rounded text-xs font-medium bg-primary/10 text-primary"
                        >
                          {dept}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'risks' && (
            <div className="space-y-3">
              {recommendations?.risks?.map((risk, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg border border-border bg-background hover:shadow-md transition-smooth"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-foreground">{risk?.title}</h4>
                    <span
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{
                        backgroundColor: `${getSeverityColor(risk?.severity)}20`,
                        color: getSeverityColor(risk?.severity)
                      }}
                    >
                      {risk?.severity?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{risk?.description}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Users" size={16} />
                    <span className="font-medium">Affected: {risk?.affected}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'engagement' && (
            <div className="space-y-3">
              {recommendations?.engagement?.map((item, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg border border-border bg-background hover:shadow-md transition-smooth"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-foreground">{item?.title}</h4>
                    <span
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{
                        backgroundColor: `${getEffortColor(item?.effort)}20`,
                        color: getEffortColor(item?.effort)
                      }}
                    >
                      {item?.effort?.toUpperCase()} EFFORT
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{item?.description}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Target" size={16} className="text-primary" />
                    <span className="font-medium text-primary">Outcome: {item?.expectedOutcome}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="AlertCircle" size={40} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No recommendations available</p>
        </div>
      )}
    </div>
  );
};

export default EnterpriseAIRecommendationsPanel;
